import { useState } from 'react';
import { endOfWeek, format, startOfWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Locale } from '@durma-soft/gros-sdk';
import { CalendarIcon } from 'lucide-react';

import { Button } from '@/components/shared/shadcn-ui/button';
import { Calendar } from '@/components/shared/shadcn-ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shared/shadcn-ui/popover';

import { cn } from '@/utils/helpers';
import { locales } from '@/config/global/languages';

interface WeekPickerProps {
  id?: string;
  weekStart?: Date;
  setWeekStart: (monday: Date) => unknown;
  disabledBefore?: Date;
  className?: string;
}

export function WeekPicker({
  id = 'week-picker',
  weekStart,
  setWeekStart,
  disabledBefore,
  className,
}: WeekPickerProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const locale = locales[i18n.language as Locale];
  const weekEnd = weekStart
    ? endOfWeek(weekStart, { weekStartsOn: 1 })
    : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild id={id}>
        <Button
          variant="outline"
          className={cn(
            'w-max justify-start gap-2 px-3 text-left font-normal',
            !weekStart && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4" />
          {weekStart && weekEnd ? (
            `${format(weekStart, 'EEE, dd.MM', { locale })} – ${format(weekEnd, 'EEE, dd.MM', { locale })}`
          ) : (
            <span>{t('menu.copy.choose-week')}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="range"
          selected={
            weekStart && weekEnd ? { from: weekStart, to: weekEnd } : undefined
          }
          onDayClick={(day) => {
            setWeekStart(startOfWeek(day, { weekStartsOn: 1 }));
            setOpen(false);
          }}
          disabled={disabledBefore ? { before: disabledBefore } : undefined}
          initialFocus
          ISOWeek
          defaultMonth={weekStart}
        />
      </PopoverContent>
    </Popover>
  );
}
